import { differenceInCalendarDays, parseISO } from 'date-fns'

import arriereBackoffice, { BookingTier } from '@avantstay/arriere-clients/dist/arriereBackoffice'

/**
 * The booking's stay-message snooze, owned by arriere and stitched onto the stay by the
 * router through `booking`. `active` is the server's verdict at read time.
 */
export const staySnoozeSelection = {
  expirationAt: true,
  active: true,
  createdAt: true,
  author: { name: true, kind: true },
} as const

const timelineEventSelection = {
  id: true,
  at: true,
  channel: true,
  medium: true,
  speaker: true,
  who: true,
  body: true,
  internal: true,
  autoOpener: true,
  isAsk: true,
  isAck: true,
  ticketId: true,
  callCode: true,
  caseId: true,
  turns: { who: true, text: true },
  deliveryStatus: true,
  deliveryError: true,
  noteId: true,
  authorId: true,
  editedAt: true,
  attachments: { id: true, url: true, fileName: true, contentType: true, size: true },
}

const stayConversationSelection = {
  booking: { id: true, hash: true, otaBookingInfo: { channel: true } },
  spokeThread: {
    total: true,
    inbound: true,
    outbound: true,
    lastInAt: true,
    awaitingReply: true,
  },
  zendeskTickets: { ticketId: true, sentiment: true },
  calls: {
    code: true,
    ticketId: true,
    at: true,
    topic: true,
    summary: true,
    sentimentRating: true,
    resolution: true,
    words: true,
    thin: true,
    direction: true,
    agentOnCall: true,
    assignee: true,
    transcript: true,
    turns: { who: true, text: true },
  },
  caseList: {
    caseId: true,
    status: true,
    isOpen: true,
    priority: true,
    taskType: true,
    categoryLabel: true,
    description: true,
    subject: true,
    owner: true,
    assignedFieldOps: true,
    locationInHome: true,
    dueDate: true,
    openedAt: true,
    openedBy: true,
    openedByAutomation: true,
    createdPreCheckin: true,
    comments: { at: true, who: true, role: true, body: true },
  },
  automatedSends: {
    __args: { includeSendsToAdditionalGuests: true },
    id: true,
    at: true,
    kind: true,
    template: true,
    status: true,
    failed: true,
    failureReason: true,
    subject: true,
    body: true,
    author: true,
    recipient: { personId: true, name: true, phones: true, emails: true, isAdditionalGuest: true },
  },
}

const getStayInboxStay = async ({ bookingId }: { bookingId: string }) => {
  return arriereBackoffice.queries.stayInboxStay({
    __alias: 'getGuestInboxStayDetail',
    __args: { bookingId },
    bookingId: true,
    dayOfStay: true,
    nightsLeft: true,
    booking: {
      stayMessageSnooze: staySnoozeSelection,
      hash: true,
      source: true,
      status: true,
      guestName: true,
      emails: true,
      phones: true,
      isRepeated: true,
      tierValue: { tier: true },
      period: { start: true, end: true },
      guests: true,
      pets: true,
      folio: { summary: { valueInDollars: true } },
      sentimentPrediction: {
        isBad: true,
        probability: true,
        confidence: true,
        inStayGuestIssue: true,
        predictionDate: true,
      },
      groupBooking: { id: true, name: true },
      home: {
        id: true,
        name: true,
        address: { city: true },
        state: { code: true },
        submarket: true,
        bedroomsCount: true,
        bathroomsCount: true,
        details: { assetType: true },
        links: { listings: { leadSource: true, url: true } },
        region: { id: true, name: true, timezone: true },
        assignedFieldOps: { name: true },
        propertyHealth: {
          vendorName: true,
          propertyRating: true,
          refundRate: true,
          maintenanceCases90d: true,
          isgiRate180d: true,
          inspectionRiskFlag: true,
          homeSpoke: {
            conversations90d: true,
            avgSentiment: true,
            unresolved: true,
            topicMaintenance: true,
            aiRiskFlags: true,
          },
        },
      },
    },
    risk: { atRisk: true, reason: true },
    cases: {
      total: true,
      open: true,
      urgentOpen: true,
      openPreCheckin: true,
      allResolved: true,
      top: {
        caseId: true,
        priority: true,
        status: true,
        owner: true,
        categoryLabel: true,
        taskType: true,
        description: true,
        openedAt: true,
        dueDate: true,
        createdPreCheckin: true,
        comments: { at: true, who: true, role: true, body: true },
      },
    },
    spoke: { inbound: true, outbound: true, firstResponder: true, responders: true },
    sends: { total: true, failed: true, neverReached: true },
    judgement: {
      evaluatedAt: true,
      band: true,
      status: true,
      conflict: true,
      blocker: true,
      assignee: true,
      scheduled: true,
      followUp: {
        state: true,
        label: true,
        needs: true,
        waitedHours: true,
        sinceHours: true,
        issueAt: true,
        lastAt: true,
        lastWho: true,
        lastVia: true,
        botAt: true,
        afterIssue: true,
      },
      exposure: { score: true, parts: { label: true, value: true, max: true, note: true } },
      watch: { points: true, why: true, isWatch: true },
      sla: {
        tier: true,
        targetMinutes: true,
        waitingMinutes: true,
        overMinutes: true,
        breached: true,
        actionable: true,
        why: true,
        queueState: true,
      },
      whyOpen: { tone: true, text: true },
    },
    readiness: { cleanMissing: true, cleanDoneLocal: true, leadHours: true },
  })
}

type StayInboxStayData = NonNullable<Awaited<ReturnType<typeof getStayInboxStay>>>
type StayInboxBooking = NonNullable<StayInboxStayData['booking']>
type StayInboxTopCase = NonNullable<StayInboxStayData['cases']['top']>

const toTopCase = ({ comments, ...topCase }: StayInboxTopCase) => {
  const lastComment = comments
    .toSorted((left, right) => {
      return String(left.at).localeCompare(String(right.at))
    })
    .at(-1)

  return { ...topCase, comments: comments.length, lastComment, lastCommentAt: lastComment?.at }
}

const toModelScore = (prediction: StayInboxBooking['sentimentPrediction']) => {
  return {
    scored: !!prediction,
    isBad: prediction?.isBad,
    goodProb: prediction ? prediction.probability * 100 : undefined,
    confidence: prediction?.confidence,
    predictionDate: prediction?.predictionDate,
    isgiCount: prediction?.inStayGuestIssue ?? 0,
  }
}

const toStayFacts = ({ booking, dayOfStay, nightsLeft, cases, ...stay }: StayInboxStayData) => {
  if (!booking) return null

  const { home, period } = booking
  const checkIn = String(period.start)
  const checkout = String(period.end)
  const listing =
    home.links.listings.find(link => link.leadSource === booking.source) ??
    home.links.listings.at(0)

  return {
    ...stay,
    booking: {
      stayMessageSnooze: booking.stayMessageSnooze,
      groupBooking: booking.groupBooking,
    },
    code: booking.hash,
    market: home.region?.name ?? '',
    marketId: home.region?.id ?? '',
    channel: booking.source,
    bookingStatus: booking.status,
    guest: {
      name: booking.guestName || undefined,
      email: booking.emails.at(0),
      phone: booking.phones.at(0),
      isVip: booking.tierValue.tier === BookingTier.vip,
      isRepeat: booking.isRepeated,
    },
    property: {
      id: home.id,
      name: home.name,
      submarket: home.submarket,
      city: home.address.city,
      state: home.state?.code,
      listingUrl: listing?.url,
      beds: home.bedroomsCount,
      baths: home.bathroomsCount,
      assetType: home.details.assetType,
      fieldOps: home.assignedFieldOps?.name,
    },
    stay: {
      checkIn,
      checkout,
      nights: differenceInCalendarDays(parseISO(checkout), parseISO(checkIn)),
      guests: booking.guests,
      pets: booking.pets,
      price: booking.folio.summary.valueInDollars,
      dayOfStay,
      nightsLeft,
      timezone: home.region?.timezone,
    },
    model: toModelScore(booking.sentimentPrediction),
    cases: { ...cases, top: cases.top ? toTopCase(cases.top) : undefined },
    propertyHealth: home.propertyHealth,
  }
}

export const getGuestInboxStayDetail = async ({ bookingId }: { bookingId: string }) => {
  const stay = await getStayInboxStay({ bookingId })

  return stay ? toStayFacts(stay) : null
}

export interface GetTimelineConversationParams {
  bookingId: string
  includeSends: boolean
  includeSendsToAdditionalGuests: boolean
}

export const getTimelineConversation = async ({
  bookingId,
  includeSends,
  includeSendsToAdditionalGuests,
}: GetTimelineConversationParams) => {
  return arriereBackoffice.queries.timelineConversation({
    __alias: 'getTimelineConversation',
    __args: { bookingId },
    liveAt: true,
    timeline: {
      __args: {
        includeInternal: true,
        includeInternalNotes: true,
        includeSystem: true,
        includeCaseNotes: true,
        includeSends,
        includeSendsToAdditionalGuests,
      },
      ...timelineEventSelection,
    },
    ...stayConversationSelection,
  })
}

export const sendStayMessage = async ({
  bookingId,
  message,
}: {
  bookingId: string
  message: string
}) => {
  return arriereBackoffice.mutations
    .stayMessage({
      __alias: 'sendGuestInboxMessage',
      sendManualMessage: {
        __args: { bookingId, message },
      },
    })
    .then(res => res.sendManualMessage)
}

export const sendOtaMessage = async ({
  bookingId,
  message,
}: {
  bookingId: string
  message: string
}) => {
  return arriereBackoffice.mutations
    .stayMessage({
      __alias: 'sendGuestInboxOtaMessage',
      sendOtaManualMessage: { __args: { bookingId, message } },
    })
    .then(res => res.sendOtaManualMessage)
}

/** Defers the stay until `expirationAt` (ISO 8601). Replaces the snooze it had. */
export const snoozeStay = async ({
  bookingId,
  expirationAt,
}: {
  bookingId: string
  expirationAt: string
}) => {
  return arriereBackoffice.mutations
    .stayMessage({
      __alias: 'snoozeGuestInboxStay',
      snooze: {
        __args: { bookingId, expirationAt },
        ...staySnoozeSelection,
      },
    })
    .then(res => res.snooze)
}

/** Wakes the stay now. False when it had no snooze to cancel. */
export const wakeStay = async ({ bookingId }: { bookingId: string }) => {
  return arriereBackoffice.mutations
    .stayMessage({
      __alias: 'wakeGuestInboxStay',
      cancelSnooze: { __args: { bookingId } },
    })
    .then(res => res.cancelSnooze)
}

export interface AddInternalNoteParams {
  bookingId: string
  body: string
}

export const addInternalNote = async ({ bookingId, body }: AddInternalNoteParams) => {
  return arriereBackoffice.mutations
    .stayMessage({
      __alias: 'addGuestInboxInternalNote',
      addInternalNote: { __args: { bookingId, body }, id: true },
    })
    .then(res => res.addInternalNote)
}

export interface UpdateInternalNoteParams {
  noteId: string
  body: string
}

export const updateInternalNote = async ({ noteId, body }: UpdateInternalNoteParams) => {
  return arriereBackoffice.mutations
    .stayMessage({
      __alias: 'updateGuestInboxInternalNote',
      updateInternalNote: { __args: { noteId, body }, id: true },
    })
    .then(res => res.updateInternalNote)
}

export interface DeleteInternalNoteParams {
  noteId: string
}

export const deleteInternalNote = async ({ noteId }: DeleteInternalNoteParams) => {
  return arriereBackoffice.mutations
    .stayMessage({
      __alias: 'deleteGuestInboxInternalNote',
      deleteInternalNote: { __args: { noteId } },
    })
    .then(res => res.deleteInternalNote)
}
